import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import { getSentryConfig, isSentryEnabled, logSentryConfig } from '@/config/sentry.resolver';

let isInitialized = false;

/**
 * Initialize Sentry error monitoring
 * Must be called before any other initialization (including GA4)
 */
export const initSentry = (): void => {
  if (isInitialized) return;

  if (!isSentryEnabled()) {
    console.log('[Sentry] Disabled for this environment');
    return;
  }

  const config = getSentryConfig();

  if (!config.dsn) {
    console.warn('[Sentry] No DSN configured. Check sentry.resolver.ts or set VITE_SENTRY_DSN');
    return;
  }

  if (config.debug) {
    logSentryConfig();
  }

  try {
    Sentry.init({
      dsn: config.dsn,
      environment: config.environment,
      release: import.meta.env.VITE_APP_VERSION || 'unknown',
      debug: config.debug,
      sendDefaultPii: true,

      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: true,
          blockAllMedia: false,
          mask: ['.sentry-mask', '[data-sentry-mask]'],
          block: ['.sentry-block', '[data-sentry-block]'],
          unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
          ignore: ['.sentry-ignore', '[data-sentry-ignore]'],
        }),
        Sentry.breadcrumbsIntegration(),
        Sentry.consoleLoggingIntegration({ levels: ['error'] }),
      ],

      enableLogs: true,
      tracesSampleRate: config.tracesSampleRate,
      profileSessionSampleRate: config.tracesSampleRate,
      replaysSessionSampleRate: config.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,

      beforeSend(event, hint) {
        const error = hint?.originalException;

        // Filter out 401 / token refresh errors
        if (error instanceof Error) {
          if (error.message?.includes('401') || error.message?.includes('Token refresh failed')) {
            return null;
          }
        }

        // Filter out Axios errors we don't want
        if (isAxiosError(error)) {
          const status = error.response?.status;
          // Drop 401s (token refresh handled by interceptor)
          if (status === 401) return null;
          // Drop tenant status errors
          const errorData = error.response?.data as any;
          const errorCode = errorData?.error?.error || errorData?.error;
          if (errorCode === 'tenant_inactive' || errorCode === 'tenant_suspended') return null;
        }

        // Filter out offline/network errors
        if (!navigator.onLine) return null;

        return event;
      },

      beforeBreadcrumb(breadcrumb) {
        // Scrub sensitive data from breadcrumbs
        if (breadcrumb.data) {
          const sensitiveKeys = ['authorization', 'bearer', 'password', 'token'];
          const data = { ...breadcrumb.data };

          for (const key of Object.keys(data)) {
            if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
              data[key] = '[REDACTED]';
            }
          }

          // Scrub sensitive values in string data
          if (typeof data.body === 'string') {
            data.body = data.body.replace(/("(?:password|token|authorization|bearer)":\s*")[^"]*"/gi, '$1[REDACTED]"');
          }

          breadcrumb.data = data;
        }
        return breadcrumb;
      },

      ignoreErrors: [
        'ResizeObserver loop',
        'Non-Error promise rejection',
        'ChunkLoadError',
        'Network Error',
        'Load failed',
        'Failed to fetch',
      ],

      denyUrls: [
        /chrome-extension:\/\//,
        /moz-extension:\/\//,
      ],
    });

    // Durable host tag — identifies the tenant/deployment before any auth is
    // resolved. Filter in Sentry UI with `host:<hostname>` alongside the
    // Environment dropdown (which holds the deployment stage).
    Sentry.setTag('host', window.location.hostname);

    isInitialized = true;
    console.log(`[Sentry] Initialized (env: ${config.environment}, host: ${window.location.hostname}, debug: ${config.debug})`);
  } catch (error) {
    console.error('[Sentry] Initialization failed:', error);
  }
};

/**
 * Check if Sentry is initialized
 */
export const isSentryInitialized = (): boolean => isInitialized;

/**
 * Set user context on Sentry scope
 */
export const setSentryUser = (user: { id: string; email?: string; name?: string } | null): void => {
  if (!isInitialized) return;

  if (user) {
    Sentry.setUser({ id: user.id, email: user.email, username: user.name });
    console.log('[Sentry] User context set:', user.id);
  } else {
    Sentry.setUser(null);
    console.log('[Sentry] User context cleared');
  }
};

/**
 * Set user_mode tag on Sentry scope.
 * Passing null clears the tag — use when logging out or on session init.
 */
export const setSentryUserMode = (mode: string | null): void => {
  if (!isInitialized) return;

  if (mode) {
    Sentry.setTag('user_mode', mode);
  } else {
    Sentry.setTag('user_mode', undefined);
  }
};

/**
 * Set tenant tag on Sentry scope
 */
export const setSentryTenant = (tenantKey: string | null): void => {
  if (!isInitialized) return;

  if (tenantKey) {
    Sentry.setTag('tenant', tenantKey);
    console.log('[Sentry] Tenant tag set:', tenantKey);
  } else {
    Sentry.setTag('tenant', undefined);
  }
};

/**
 * Capture an API error with structured context
 */
export const captureApiError = (
  error: AxiosError,
  context?: Record<string, unknown>
): void => {
  if (!isInitialized) return;

  Sentry.withScope((scope) => {
    scope.setTag('api_error', true);

    const method = (error.config?.method || 'GET').toUpperCase();
    const url = error.config?.url || 'unknown';
    const status = error.response?.status || 0;

    scope.setFingerprint([method, url, String(status)]);
    scope.setExtras({
      method,
      url,
      status,
      response: truncate(JSON.stringify(error.response?.data), 1000),
      ...context,
    });

    Sentry.captureException(error);
  });
};

// ============================================
// METRICS
// ============================================

/**
 * Increment a counter metric
 */
export const trackCount = (name: string, value: number = 1, tags?: Record<string, string>): void => {
  if (!isInitialized) return;
  Sentry.metrics.count(name, value, { tags });
};

/**
 * Track a gauge metric (current value, e.g. page load time)
 */
export const trackGauge = (name: string, value: number, tags?: Record<string, string>): void => {
  if (!isInitialized) return;
  Sentry.metrics.gauge(name, value, { tags });
};

/**
 * Track a distribution metric (e.g. response times)
 */
export const trackDistribution = (name: string, value: number, tags?: Record<string, string>): void => {
  if (!isInitialized) return;
  Sentry.metrics.distribution(name, value, { tags });
};

// ============================================
// BREADCRUMBS
// ============================================

/**
 * Add a breadcrumb
 */
export const addBreadcrumb = (
  category: string,
  message: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
): void => {
  if (!isInitialized) return;

  Sentry.addBreadcrumb({ category, message, data, level });
};

// Helpers

function isAxiosError(error: unknown): error is AxiosError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isAxiosError' in error &&
    (error as AxiosError).isAxiosError === true
  );
}

function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
}

const sentry = {
  init: initSentry,
  isInitialized: isSentryInitialized,
  setUser: setSentryUser,
  setTenant: setSentryTenant,
  captureApiError,
  addBreadcrumb,
  metrics: {
    count: trackCount,
    gauge: trackGauge,
    distribution: trackDistribution,
  },
};

export default sentry;
